/**
 * Your data — protection, export, import, deletion.
 * FR-111, FR-601..FR-603, FR-606, FR-607, S-04, S-10, L-06, L-10.
 *
 * Charter goals G3 and G4 live on this screen: the user can leave with
 * everything, and the user can destroy everything. Both are reachable without
 * digging, because hiding either would contradict the premise — an app that made
 * leaving hard would be asking to be trusted rather than earning it.
 */
import { useEffect, useRef, useState } from 'react'
import { t } from '@copy/index'
import { useEntries, useLock, useSettings } from '@contexts/index'
import {
  buildExport,
  encryptExport,
  isEncryptedExport,
  previewImport,
  applyImport,
  decryptExport,
  secretStrength,
  serialise,
  wipeEverything,
  type ImportPreview,
} from '@shared/data/private'
import { Notice } from '@shared/ui'
import { SyncSection } from './SyncSection'
import { PAYWALL_ENABLED, POLAR_CHECKOUT_URL, getCustomerLicenseKeys } from '@shared/utils/polar'

const APP_VERSION = '0.4.13'
/** Typed confirmation, so deleting everything cannot be a mis-tap. */
const DELETE_PHRASE = 'DELETE'

export function DataPage() {
  const { statistics, reload } = useEntries()
  const { phase, setPin, removePin, lockNow } = useLock()
  const mainRef = useRef<HTMLElement>(null)

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('checkout_id') || params.get('customer_session_token')) {
      document.getElementById('subscription')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    } else if (window.location.hash === '#protection') {
      document.getElementById('protection')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    } else {
      mainRef.current?.scrollIntoView({ block: 'start' })
    }
  }, [])

  return (
    <main ref={mainRef} className="flex flex-col gap-8 p-4">
      <h1 className="text-xl font-medium">{t('nav.data')}</h1>

      <ProtectionSection
        phase={phase}
        onSetPin={setPin}
        onRemovePin={removePin}
        onLock={lockNow}
      />
      {PAYWALL_ENABLED && <SubscriptionSection />}
      <SyncSection />
      <ExportSection entryCount={statistics.daysLogged} />
      <ImportSection onImported={reload} />
      <DeleteSection entryCount={statistics.daysLogged} onDeleted={reload} />

      <footer className="text-center text-xs text-ink-muted mt-4">
        Version {APP_VERSION}
      </footer>
    </main>
  )
}

function SubscriptionSection() {
  const {
    settings,
    isPremium,
    premiumLoading,
    premiumError,
    activatePremium,
    deactivatePremium,
  } = useSettings()
  const { entries } = useEntries()
  const [key, setKey] = useState('')
  const [busy, setBusy] = useState(false)
  const [successMsg, setSuccessMsg] = useState<string | null>(null)
  const [checkoutId, setCheckoutId] = useState<string | null>(null)

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const id = params.get('checkout_id')
    if (id) {
      setCheckoutId(id)
    }
    const token = params.get('customer_session_token')
    if (token && !isPremium) {
      setBusy(true)
      getCustomerLicenseKeys(token)
        .then(async (keys) => {
          if (keys.length > 0) {
            const firstKey = keys[0]
            if (firstKey) {
              const success = await activatePremium(firstKey)
              if (success) {
                setSuccessMsg('License activated automatically from your purchase!')
                window.history.replaceState({}, document.title, window.location.pathname)
              } else {
                setSuccessMsg('Could not activate license key automatically. Please enter it manually.')
              }
            }
          } else {
            setSuccessMsg('Could not find a license key in your checkout session. Please check your email and enter it below.')
          }
          setBusy(false)
        })
        .catch((e) => {
          console.error(e)
          setSuccessMsg('Error verifying license key. Please check your email and enter it below.')
          setBusy(false)
        })
    }
  }, [isPremium, activatePremium])

  const uniqueDaysCount = entries.size
  const trialPercentage = Math.min(100, Math.round((uniqueDaysCount / 90) * 100))

  const handleActivate = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setSuccessMsg(null)
    const success = await activatePremium(key)
    setBusy(false)
    if (success) {
      setKey('')
      setSuccessMsg('License activated successfully!')
    }
  }

  const handleDeactivate = async () => {
    if (confirm('Are you sure you want to deactivate premium on this device?')) {
      setBusy(true)
      setSuccessMsg(null)
      await deactivatePremium()
      setBusy(false)
    }
  }

  const checkoutUrl = POLAR_CHECKOUT_URL

  return (
    <section id="subscription" className="flex flex-col gap-3 border-t border-line pt-6">
      <h2 className="text-sm font-medium">Subscription</h2>

      {isPremium ? (
        <div className="flex flex-col gap-3">
          <Notice tone="info">
            Premium Active. Thank you for supporting Thana!
          </Notice>
          <div className="flex flex-col gap-1 text-xs text-ink-muted">
            <p>
              License Key:{' '}
              <span className="font-mono">
                {settings.premium?.licenseKey.slice(0, 8)}...
                {settings.premium?.licenseKey.slice(-4)}
              </span>
            </p>
            <p>
              Last Validated:{' '}
              {settings.premium?.lastValidated
                ? new Date(settings.premium.lastValidated).toLocaleDateString()
                : 'N/A'}
            </p>
          </div>
          <button
            type="button"
            onClick={() => void handleDeactivate()}
            disabled={busy || premiumLoading}
            className="min-h-11 w-full rounded-lg border border-line text-sm disabled:opacity-50 transition-all duration-200 hover:bg-line/40 active:scale-[0.98]"
          >
            Deactivate License
          </button>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {checkoutId && (
            <Notice tone="info">
              Thank you for your purchase! Your license key was shown on the checkout page and sent to your email. Please enter it below.
            </Notice>
          )}
          {uniqueDaysCount >= 90 ? (
            <Notice tone="warning">
              You have reached the free limit of 90 tracking days. Upgrade to continue logging new entries.
            </Notice>
          ) : (
            <div className="flex flex-col gap-1">
              <div className="flex justify-between text-xs text-ink-muted">
                <span>Free Trial Progress</span>
                <span>{uniqueDaysCount} / 90 days used</span>
              </div>
              <div className="h-2 w-full overflow-hidden rounded bg-line">
                <div
                  className="h-full bg-accent transition-all duration-300"
                  style={{ width: `${trialPercentage}%` }}
                />
              </div>
            </div>
          )}

          <a
            href={checkoutUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex min-h-11 items-center justify-center rounded-lg bg-accent text-sm font-medium text-accent-ink hover:opacity-90"
          >
            Subscribe on Polar.sh ($12/year + tax)
          </a>

          <form onSubmit={(e) => void handleActivate(e)} className="flex flex-col gap-2">
            <label htmlFor="license-key" className="text-sm">
              Already have a license key? Enter it here:
            </label>
            <input
              id="license-key"
              type="text"
              placeholder="XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              className="min-h-11 rounded-lg border border-line bg-surface-raised px-3 text-sm font-mono transition-all duration-200 hover:border-ink-muted/50 focus:border-accent focus:outline-none"
              disabled={busy || premiumLoading}
            />
            {premiumError && <Notice tone="error">{premiumError}</Notice>}
            {successMsg && <Notice tone="info">{successMsg}</Notice>}
            <button
              type="submit"
              disabled={busy || premiumLoading || !key.trim()}
              className="min-h-11 rounded-lg border border-line text-sm disabled:opacity-50 transition-all duration-200 hover:bg-line/40 active:scale-[0.98]"
            >
              {busy || premiumLoading ? 'Activating...' : 'Activate License'}
            </button>
          </form>
        </div>
      )}
    </section>
  )
}

function ProtectionSection({
  phase,
  onSetPin,
  onRemovePin,
  onLock,
}: {
  phase: string
  onSetPin: (pin: string) => Promise<void>
  onRemovePin: (pin: string) => Promise<boolean>
  onLock: () => void
}) {
  const [pin, setPinValue] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string>()

  const protected_ = phase !== 'unprotected'
  const strength = pin === '' ? undefined : secretStrength(pin)

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    setError(undefined)
    if (protected_) {
      setBusy(true)
      const ok = await onRemovePin(pin)
      setBusy(false)
      if (!ok) setError(t('data.protection.wrongPin'))
      setPinValue('')
      return
    }
    if (pin !== confirm) {
      setError(t('data.protection.mismatch'))
      return
    }
    setBusy(true)
    await onSetPin(pin)
    setBusy(false)
    setPinValue('')
    setConfirm('')
  }

  return (
    <section className="flex flex-col gap-3">
      <h2 id="protection" className="text-sm font-medium">{t('data.protection.heading')}</h2>

      {/* The honest description of each state. With no PIN the key sits beside
          the data, and calling that protection would be the overclaim this
          project exists to avoid (ADR-0006). */}
      <Notice tone={protected_ ? 'info' : 'warning'}>
        {protected_ ? t('data.protection.on') : t('data.protection.off')}
      </Notice>

      <form onSubmit={(event) => void submit(event)} className="flex flex-col gap-2">
        <label htmlFor="new-pin" className="text-sm">
          {protected_ ? t('data.protection.currentPin') : t('data.protection.newPin')}
        </label>
        <input
          id="new-pin"
          type="password"
          autoComplete="new-password"
          value={pin}
          onChange={(event) => setPinValue(event.target.value)}
          className="min-h-11 rounded-lg border border-line bg-surface-raised px-3 transition-all duration-200 hover:border-ink-muted/50 focus:border-accent focus:outline-none"
        />

        {!protected_ ? (
          <>
            <label htmlFor="confirm-pin" className="text-sm">
              {t('data.protection.confirmPin')}
            </label>
            <input
              id="confirm-pin"
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(event) => setConfirm(event.target.value)}
              className="min-h-11 rounded-lg border border-line bg-surface-raised px-3 transition-all duration-200 hover:border-ink-muted/50 focus:border-accent focus:outline-none"
            />
            {strength !== undefined ? (
              // Informational, never a gate. A green bar implying safety is the
              // pattern this avoids (T-10) — so the text says what the limit is.
              <p className="text-xs text-ink-muted">
                {t(`data.protection.strength.${strength}`)}
              </p>
            ) : null}
            {/* Said BEFORE the PIN is set, not after. */}
            <Notice tone="warning">{t('data.protection.noRecovery')}</Notice>
          </>
        ) : null}

        {error !== undefined ? <Notice tone="error">{error}</Notice> : null}

        <button
          type="submit"
          disabled={busy || pin === ''}
          className="min-h-11 rounded-lg border border-line text-sm disabled:opacity-50 transition-all duration-200 hover:bg-line/40 active:scale-[0.98]"
        >
          {busy
            ? t('data.protection.working')
            : protected_
              ? t('data.protection.remove')
              : t('data.protection.set')}
        </button>
      </form>

      {protected_ ? (
        <button
          type="button"
          onClick={onLock}
          className="min-h-11 rounded-lg border border-line text-sm transition-all duration-200 hover:bg-line/40 active:scale-[0.98]"
        >
          {t('data.protection.lockNow')}
        </button>
      ) : null}
    </section>
  )
}

function ExportSection({ entryCount }: { entryCount: number }) {
  const [passphrase, setPassphrase] = useState('')
  const [busy, setBusy] = useState(false)

  const download = (contents: string, suffix: string) => {
    const blob = new Blob([contents], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    // No date in the filename: a file called thana-2026-08-17.json in a shared
    // Downloads folder says more than it needs to.
    link.download = `thana-export${suffix}.json`
    link.click()
    URL.revokeObjectURL(url)
  }

  const exportEncrypted = async () => {
    setBusy(true)
    const document_ = await buildExport(APP_VERSION, new Date())
    download(JSON.stringify(await encryptExport(document_, passphrase), null, 2), '-encrypted')
    setBusy(false)
    setPassphrase('')
  }

  const exportPlain = async () => {
    setBusy(true)
    download(serialise(await buildExport(APP_VERSION, new Date())), '')
    setBusy(false)
  }

  return (
    <section className="flex flex-col gap-3 border-t border-line pt-6">
      <h2 className="text-sm font-medium">{t('data.export.heading')}</h2>
      <p className="text-xs text-ink-muted">{t('data.export.body', { count: entryCount })}</p>

      <label htmlFor="export-passphrase" className="text-sm">
        {t('data.export.passphrase')}
      </label>
      <input
        id="export-passphrase"
        type="password"
        autoComplete="new-password"
        value={passphrase}
        onChange={(event) => setPassphrase(event.target.value)}
        className="min-h-11 rounded-lg border border-line bg-surface-raised px-3 transition-all duration-200 hover:border-ink-muted/50 focus:border-accent focus:outline-none"
      />
      <p className="text-xs text-ink-muted">{t('data.export.passphraseHint')}</p>

      {/* Encrypted is the primary action (FR-602, S-10). */}
      <button
        type="button"
        onClick={() => void exportEncrypted()}
        disabled={busy || passphrase === ''}
        className="min-h-11 rounded-lg bg-accent text-sm text-accent-ink disabled:opacity-50 transition-all duration-200 hover:opacity-90 active:scale-[0.98]"
      >
        {t('data.export.encrypted')}
      </button>

      {/* Plaintext stays available for portability — GDPR Article 20 is a right,
          not a feature we get to withhold — behind an explicit warning
          (FR-603). */}
      <Notice tone="warning">{t('data.export.plainWarning')}</Notice>
      <button
        type="button"
        onClick={() => void exportPlain()}
        disabled={busy}
        className="min-h-11 rounded-lg border border-line text-sm disabled:opacity-50 transition-all duration-200 hover:bg-line/40 active:scale-[0.98]"
      >
        {t('data.export.plain')}
      </button>
    </section>
  )
}

function ImportSection({ onImported }: { onImported: () => Promise<void> }) {
  const fileInput = useRef<HTMLInputElement>(null)
  const [preview, setPreview] = useState<ImportPreview>()
  const [passphrase, setPassphrase] = useState('')
  const [pendingRaw, setPendingRaw] = useState<string>()
  const [error, setError] = useState<string>()
  const [busy, setBusy] = useState(false)

  const readFile = async (file: File) => {
    setError(undefined)
    setPreview(undefined)
    const raw = await file.text()

    try {
      const parsed: unknown = JSON.parse(raw)
      if (isEncryptedExport(parsed)) {
        // Hold it until the passphrase arrives rather than failing outright.
        setPendingRaw(raw)
        return
      }
      setPreview(await previewImport(raw))
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t('data.import.unreadable'))
    }
  }

  const unlockFile = async () => {
    if (pendingRaw === undefined) return
    setBusy(true)
    try {
      const parsed = JSON.parse(pendingRaw) as unknown
      if (!isEncryptedExport(parsed)) throw new Error(t('data.import.unreadable'))
      const document_ = await decryptExport(parsed, passphrase)
      setPreview(await previewImport(JSON.stringify(document_)))
      setPendingRaw(undefined)
      setPassphrase('')
    } catch {
      setError(t('data.import.wrongPassphrase'))
    }
    setBusy(false)
  }

  const apply = async (mode: 'merge' | 'replace') => {
    if (preview === undefined) return
    setBusy(true)
    await applyImport(preview, mode)
    await onImported()
    setBusy(false)
    setPreview(undefined)
    if (fileInput.current) fileInput.current.value = ''
  }

  return (
    <section className="flex flex-col gap-3 border-t border-line pt-6">
      <h2 className="text-sm font-medium">{t('data.import.heading')}</h2>

      <input
        ref={fileInput}
        type="file"
        accept="application/json,.json"
        aria-label={t('data.import.choose')}
        onChange={(event) => {
          const file = event.target.files?.[0]
          if (file) void readFile(file)
        }}
        className="text-sm file:mr-3 file:min-h-11 file:rounded-lg file:border file:border-line file:bg-surface-raised file:px-4 file:text-xs file:font-medium file:text-ink hover:file:bg-line/40 file:cursor-pointer transition-all duration-200 file:active:scale-[0.98] cursor-pointer"
      />

      {pendingRaw !== undefined ? (
        <>
          <label htmlFor="import-passphrase" className="text-sm">
            {t('data.import.passphrase')}
          </label>
          <input
            id="import-passphrase"
            type="password"
            value={passphrase}
            onChange={(event) => setPassphrase(event.target.value)}
            className="min-h-11 rounded-lg border border-line bg-surface-raised px-3 transition-all duration-200 hover:border-ink-muted/50 focus:border-accent focus:outline-none"
          />
          <button
            type="button"
            onClick={() => void unlockFile()}
            disabled={busy || passphrase === ''}
            className="min-h-11 rounded-lg border border-line text-sm disabled:opacity-50 transition-all duration-200 hover:bg-line/40 active:scale-[0.98]"
          >
            {t('data.import.unlockFile')}
          </button>
        </>
      ) : null}

      {error !== undefined ? <Notice tone="error">{error}</Notice> : null}

      {preview !== undefined ? (
        <>
          {/* FR-606: the counts come BEFORE anything is applied. Import is the
              one operation that can destroy data a user cannot get back, so it
              is a decision rather than a hope. */}
          <Notice tone="info">
            {t('data.import.preview', {
              added: preview.newEntries,
              changed: preview.changedEntries,
              same: preview.unchangedEntries,
            })}
          </Notice>
          <button
            type="button"
            onClick={() => void apply('merge')}
            disabled={busy}
            className="min-h-11 rounded-lg bg-accent text-sm text-accent-ink disabled:opacity-50 transition-all duration-200 hover:opacity-90 active:scale-[0.98]"
          >
            {t('data.import.merge')}
          </button>
          <button
            type="button"
            onClick={() => void apply('replace')}
            disabled={busy}
            className="min-h-11 rounded-lg border border-line text-sm disabled:opacity-50 transition-all duration-200 hover:bg-line/40 active:scale-[0.98]"
          >
            {t('data.import.replace')}
          </button>
          <p className="text-xs text-ink-muted">{t('data.import.modeHint')}</p>
        </>
      ) : null}
    </section>
  )
}

function DeleteSection({
  entryCount,
  onDeleted,
}: {
  entryCount: number
  onDeleted: () => Promise<void>
}) {
  const [typed, setTyped] = useState('')
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(false)

  const deleteEverything = async () => {
    setBusy(true)
    // One call, because the sequence has an order and a completeness
    // requirement that a button handler should not own. See wipe.ts — including
    // the bug that made it a single function.
    await wipeEverything()
    await onDeleted()
    setBusy(false)
    setDone(true)
    setTyped('')
    // A reload rebuilds a fresh keyring, so the app is usable again immediately
    // rather than sitting in a half-destroyed state.
    location.reload()
  }

  return (
    <section className="flex flex-col gap-3 border-t border-line pt-6">
      <h2 className="text-sm font-medium">{t('data.delete.heading')}</h2>
      <p className="text-xs text-ink-muted">{t('data.delete.body', { count: entryCount })}</p>

      {/* The honest limit of deletion in this architecture (T-12, legal map
          Section 3.3). Promising more than this would be the easiest lie in the
          whole app to tell. */}
      <Notice tone="warning">{t('data.delete.honest')}</Notice>

      <label htmlFor="delete-confirm" className="text-sm">
        {t('data.delete.typeToConfirm', { phrase: DELETE_PHRASE })}
      </label>
      <input
        id="delete-confirm"
        type="text"
        autoComplete="off"
        value={typed}
        onChange={(event) => setTyped(event.target.value)}
        className="min-h-11 rounded-lg border border-line bg-surface-raised px-3 transition-all duration-200 hover:border-ink-muted/50 focus:border-accent focus:outline-none"
      />
      <button
        type="button"
        onClick={() => void deleteEverything()}
        disabled={busy || typed !== DELETE_PHRASE}
        className="min-h-11 rounded-lg border border-flow-heavy text-sm text-flow-heavy disabled:opacity-50 transition-all duration-200 hover:bg-flow-heavy/10 active:scale-[0.98]"
      >
        {t('data.delete.confirm')}
      </button>

      {done ? <Notice tone="info">{t('data.delete.done')}</Notice> : null}
    </section>
  )
}
